/* Authentication is never mocked.
 *
 * Screen data still comes from `mockApi` until the ingest is live, but who you
 * are does not: `/api/me` is always a real request against the real server, so
 * the sign-in gate cannot be satisfied by a fixture. A mock that can log you in
 * is a mock that will eventually ship.
 */

export type AuthState =
  | { status: 'checking' }
  /** No valid session. The server said so; we did not guess. */
  | { status: 'anonymous'; reason?: string }
  /** Signed in, and the registry admitted them. */
  | { status: 'signed-in'; me: MeResponse }
  /** Signed in to Microsoft, but refused entry — a different thing entirely. */
  | { status: 'refused'; error: string; message: string }
  /** We could not reach the server. Not the same as "not signed in". */
  | { status: 'unreachable'; detail: string };

export interface MeResponse {
  displayName: string;
  isAdmin: boolean;
  modules: string[];
  entityCount: number;
  orgWide: boolean;
}

export async function fetchMe(signal?: AbortSignal): Promise<AuthState> {
  let res: Response;
  try {
    res = await fetch('/api/me', {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
      signal,
    });
  } catch (e) {
    // A network failure must not render as "signed out", or a blip would
    // bounce someone to a sign-in page they do not need.
    return { status: 'unreachable', detail: e instanceof Error ? e.message : String(e) };
  }

  if (res.status === 401) return { status: 'anonymous' };

  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    return {
      status: 'refused',
      error: String(body.error ?? 'forbidden'),
      message: String(body.message ?? 'This account may not use the application.'),
    };
  }

  if (!res.ok) {
    return { status: 'unreachable', detail: `server returned ${res.status}` };
  }

  const raw = await res.json();
  return {
    status: 'signed-in',
    me: {
      displayName: String(raw.display_name ?? raw.displayName ?? ''),
      isAdmin: Boolean(raw.is_admin ?? raw.isAdmin),
      modules: Array.isArray(raw.modules) ? raw.modules.map(String) : [],
      entityCount: Number(raw.entity_count ?? raw.entityCount ?? 0),
      orgWide: Boolean(raw.org_wide ?? raw.orgWide),
    },
  };
}

export const SIGN_IN_URL = '/auth/login';
export const SIGN_OUT_URL = '/auth/logout';
